import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Divider
} from '@mui/material';
import { Equipment, EquipmentFormData, Model, Brand } from '../../types';
import equipmentService from '../../services/equipmentService';
import FornecedorSelect from '../selects/FornecedorSelect';
import EscritorioSelect from '../selects/EscritorioSelect';
import { useAuth } from '../../contexts/AuthContext';

interface EquipmentFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  equipment?: Equipment | null;
}

const EquipmentForm: React.FC<EquipmentFormProps> = ({
  open,
  onClose,
  onSuccess,
  equipment
}) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState<EquipmentFormData>({
    modeloId: 0,
    numero_serie: '',
    gpon_sn: '',
    mac_address: '',
    modalidade: 'comodato',
    observacoes: '',

    ativo: true,
    // Campo para escritório
    escritorio_id: null,
    // Novos campos para controle de fornecedor e estoque
    fornecedor_id: null,
    data_aquisicao: '',
    num_documento: '',
    end_estoque: ''
  });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<number>(0);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modalidades = [
    { value: 'proprio', label: 'Próprio' },
    { value: 'comodato', label: 'Comodato' }
  ];



  // Função para obter o primeiro escritório do usuário logado
  const getUserDefaultEscritorioId = (): number | undefined => {
    if (!user?.escritorio_ids) {
      // Se o usuário não tem escritórios específicos, retornar undefined
      // O componente EscritorioSelect vai mostrar todos os escritórios disponíveis
      return undefined;
    }
    
    // Se escritorio_ids é string JSON, fazer parse
    let escritorioIds: number[] = [];
    if (typeof user.escritorio_ids === 'string') {
      try {
        escritorioIds = JSON.parse(user.escritorio_ids);
      } catch (error) {
        console.error('Erro ao fazer parse do escritorio_ids:', error);
        return undefined;
      }
    } else if (Array.isArray(user.escritorio_ids)) {
      escritorioIds = user.escritorio_ids;
    } else {
      escritorioIds = [user.escritorio_ids];
    }
    
    // Retornar o primeiro escritório da lista
    return escritorioIds.length > 0 ? escritorioIds[0] : undefined;
  };

  // Carregar dados iniciais
  useEffect(() => {
    if (open) {
      loadBrands();
      loadModels();
    }
  }, [open]);

  const loadBrands = async () => {
    try {
      const response = await equipmentService.getBrands();
      if (response.success && response.data) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
    }
  };

  const loadModels = async () => {
    try {
      const response = await equipmentService.getModels();
      if (response.success && response.data) {
        setModels(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    }
  };

  const handleInputChange = (field: keyof EquipmentFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Se o modelo mudou, encontrar o modelo selecionado
    if (field === 'modeloId') {
      const model = models.find(m => m.id === value);
      setSelectedModel(model || null);
      
      // Limpar GPON SN se não for ONU/ONT
      if (model && !equipmentService.isGponSnRequired(model.tipo)) {
        setFormData(prev => ({ ...prev, gpon_sn: '' }));
      }
    }
    
    // Limpar erro quando o usuário começar a digitar
    if (error) {
      setError(null);
    }
  };

  const handleBrandChange = (brandId: number) => {
    setSelectedBrand(brandId);
    
    // Filtrar modelos pela marca selecionada
    if (brandId === 0) {
      setFilteredModels([]);
    } else {
      const brandModels = models.filter(model => 
        model.marcaId === brandId || model.marcaId === brandId
      );
      setFilteredModels(brandModels);
    }
    
    // Limpar modelo selecionado quando trocar de marca
    setFormData(prev => ({ ...prev, modeloId: 0 }));
    setSelectedModel(null);
    
    // Limpar erro
    if (error) {
      setError(null);
    }
  };

  const handleMacChange = (value: string) => {
    // Formatar automaticamente o MAC
    const formattedMac = value.toUpperCase().replace(/[^A-F0-9]/g, '');
    let finalMac = '';
    
    for (let i = 0; i < formattedMac.length && i < 12; i++) {
      if (i > 0 && i % 2 === 0) {
        finalMac += ':';
      }
      finalMac += formattedMac[i];
    }
    
    setFormData(prev => ({
      ...prev,
      mac_address: finalMac
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.modeloId) {
      setError('O modelo é obrigatório');
      return false;
    }

    if (!formData.numero_serie.trim()) {
      setError('O número de série é obrigatório');
      return false;
    }

    if (formData.numero_serie.trim().length < 3) {
      setError('O número de série deve ter pelo menos 3 caracteres');
      return false;
    }

    if (!formData.modalidade) {
      setError('A modalidade é obrigatória');
      return false;
    }

    // Validar GPON SN se for ONU/ONT
    if (selectedModel && equipmentService.isGponSnRequired(selectedModel.tipo)) {
      if (!formData.gpon_sn?.trim()) {
        setError('GPON SN é obrigatório para equipamentos ONU/ONT');
        return false;
      }
      
      if (formData.gpon_sn.trim().length < 8) {
        setError('GPON SN deve ter pelo menos 8 caracteres');
        return false;
      }
    }

    // Validar MAC se fornecido
    if (formData.mac_address && !equipmentService.validateMac(formData.mac_address)) {
      setError('Formato de MAC inválido. Use o formato XX:XX:XX:XX:XX:XX');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
             const dataToSend = {
         modeloId: formData.modeloId,
         numero_serie: equipmentService.formatSerial(formData.numero_serie.trim()),
         gpon_sn: formData.gpon_sn?.trim() || undefined,
         mac_address: formData.mac_address ? equipmentService.formatMac(formData.mac_address) : undefined,
         modalidade: formData.modalidade,

         observacoes: formData.observacoes?.trim() || undefined,
         ativo: formData.ativo,
         // Campo para escritório
         escritorio_id: formData.escritorio_id || undefined,
         // Novos campos para controle de fornecedor e estoque
         fornecedor_id: formData.fornecedor_id || undefined,
         data_aquisicao: formData.data_aquisicao || undefined,
         num_documento: formData.num_documento?.trim() || undefined,
         end_estoque: formData.end_estoque?.trim() || undefined
       };

      if (equipment) {
        await equipmentService.updateEquipment(equipment.id, dataToSend);
      } else {
        await equipmentService.createEquipment(dataToSend);
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Erro ao salvar equipamento:', error);
      setError(error?.response?.data?.message || 'Erro ao salvar equipamento');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      modeloId: 0,
      numero_serie: '',
      gpon_sn: '',
      mac_address: '',
      modalidade: 'comodato',

      observacoes: '',
      ativo: true,
      // Campo para escritório
      escritorio_id: null,
      // Novos campos para controle de fornecedor e estoque
      fornecedor_id: null,
      data_aquisicao: '',
      num_documento: '',
      end_estoque: ''
    });
    setSelectedBrand(0);
    setFilteredModels([]);
    setSelectedModel(null);
    setError(null);
    onClose();
  };

  // Resetar form quando abrir para edição
  useEffect(() => {
    if (open && equipment) {
      setFormData({
        modeloId: equipment.modeloId,
        numero_serie: equipment.numero_serie,
        gpon_sn: equipment.gpon_sn || '',
        mac_address: equipment.mac_address || '',
        modalidade: equipment.modalidade,

        observacoes: equipment.observacoes || '',
        ativo: equipment.ativo,
        // Campo para escritório
        escritorio_id: equipment.escritorio_id || null,
        // Novos campos para controle de fornecedor e estoque
        fornecedor_id: equipment.fornecedor_id || null,
        data_aquisicao: equipment.data_aquisicao || '',
        num_documento: equipment.num_documento || '',
        end_estoque: equipment.end_estoque || ''
      });
      
      // Encontrar modelo selecionado
      const model = models.find(m => m.id === equipment.modeloId);
      setSelectedModel(model || null);
      
      // Se estiver editando, definir a marca baseada no modelo
      if (model) {
        const brandId = model.marcaId || model.marcaId;
        setSelectedBrand(brandId);
        const brandModels = models.filter(m => 
          (m.marcaId === brandId || model.marcaId === brandId)
        );
        setFilteredModels(brandModels);
      }
    } else if (open && !equipment) {
      // Obter o primeiro escritório do usuário logado para novo equipamento
      const defaultEscritorioId = getUserDefaultEscritorioId();
      
      setFormData({
        modeloId: 0,
        numero_serie: '',
        gpon_sn: '',
        mac_address: '',
        modalidade: 'comodato',

        observacoes: '',
        ativo: true,
        // Campo para escritório com valor padrão
        escritorio_id: defaultEscritorioId || null,
        // Novos campos para controle de fornecedor e estoque
        fornecedor_id: null,
        data_aquisicao: '',
        num_documento: '',
        end_estoque: ''
      });
      setSelectedBrand(0);
      setFilteredModels([]);
      setSelectedModel(null);
    }
  }, [open, equipment, models, user]);



  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {equipment ? 'Editar Equipamento' : 'Cadastrar Equipamento'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 1.5 }}>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={1.5}>
              {/* Seleção de Escritório */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
                  Escritório
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <EscritorioSelect
                  value={formData.escritorio_id}
                  onChange={(id) => handleInputChange('escritorio_id', id)}
                  label="Escritório"
                  placeholder="Selecione o escritório"
                  size="small"
                  fullWidth
                  required={true}
                  onlyActive={true}
                  allowClear={false}
                />
              </Grid>

              {/* Seleção de Marca e Modelo */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
                  Modelo do Equipamento
                </Typography>
              </Grid>

              {/* Seleção de Marca */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Marca</InputLabel>
                  <Select
                    value={selectedBrand}
                    onChange={(e) => handleBrandChange(Number(e.target.value))}
                    label="Marca"
                    disabled={loading}
                    size="small"
                  >
                    <MenuItem value={0}>Selecione uma marca</MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.titulo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Seleção de Modelo */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Modelo</InputLabel>
                  <Select
                    value={formData.modeloId}
                    onChange={(e) => handleInputChange('modeloId', e.target.value)}
                    label="Modelo"
                    disabled={loading || selectedBrand === 0}
                    size="small"
                  >
                    <MenuItem value={0}>
                      {selectedBrand === 0 ? 'Selecione uma marca primeiro' : 'Selecione um modelo'}
                    </MenuItem>
                    {filteredModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.titulo} ({equipmentService.getTipoLabel(model.tipo)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Informações do modelo selecionado */}
              {selectedModel && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Marca:</strong> {selectedModel.marca?.titulo} | 
                      <strong> Tipo:</strong> {equipmentService.getTipoLabel(selectedModel.tipo)} | 
                      <strong> Portas LAN:</strong> {selectedModel.portas_lan || selectedModel.portas_lan}
                      {selectedModel.portas_fxs && selectedModel.portas_fxs > 0 && (
                        <span> | <strong>Portas FXS:</strong> {selectedModel.portas_fxs}</span>
                      )}
                      {selectedModel.frequencia_wifi && (
                        <span> | <strong>WiFi:</strong> {selectedModel.frequencia_wifi}</span>
                      )}
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Informações do Equipamento */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Dados do Equipamento
                </Typography>
              </Grid>

              {/* Número de Série */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número de Série"
                  value={formData.numero_serie}
                  onChange={(e) => handleInputChange('numero_serie', e.target.value)}
                  required
                  placeholder="Ex: 123456789ABC"
                  helperText="Número de série do equipamento"
                  disabled={loading}
                  size="small"
                />
              </Grid>

              {/* Modalidade */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Modalidade</InputLabel>
                  <Select
                    value={formData.modalidade}
                    onChange={(e) => handleInputChange('modalidade', e.target.value)}
                    label="Modalidade"
                    disabled={loading}
                    size="small"
                  >
                    {modalidades.map((modalidade) => (
                      <MenuItem key={modalidade.value} value={modalidade.value}>
                        {modalidade.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>



              {/* GPON SN - apenas para ONU/ONT */}
              {selectedModel && equipmentService.isGponSnRequired(selectedModel.tipo) && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="GPON SN"
                    inputProps={{ maxLength: 12 }}
                    value={formData.gpon_sn}
                    onChange={(e) => handleInputChange('gpon_sn', e.target.value)}
                    required
                    placeholder="Ex: ALCL12345678"
                    helperText="Obrigatório para ONU/ONT, Max. 12 Digitos"
                    disabled={loading}
                    size="small"
                  />
                </Grid>
              )}

              {/* MAC Address */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="MAC do equipamento (opcional)"
                  value={formData.mac_address}
                  onChange={(e) => handleMacChange(e.target.value)}
                  placeholder="Ex: AA:BB:CC:DD:EE:FF"
                  disabled={loading}
                  inputProps={{ maxLength: 17 }}
                  size="small"
                />
              </Grid>
              

              {/* Informações de Fornecedor e Estoque */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Informações de Fornecedor e Estoque
                </Typography>
              </Grid>

              {/* Fornecedor */}
              <Grid item xs={12} sm={6}>
                <FornecedorSelect
                  value={formData.fornecedor_id}
                  onChange={(id) => handleInputChange('fornecedor_id', id)}
                  label="Fornecedor"
                  placeholder="Selecione um fornecedor"
                  size="small"
                  fullWidth
                />
              </Grid>

              {/* Data de Aquisição */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Data de Aquisição"
                  type="date"
                  value={formData.data_aquisicao}
                  onChange={(e) => handleInputChange('data_aquisicao', e.target.value)}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={loading}
                />
              </Grid>

              {/* Número do Documento */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número do Documento"
                  value={formData.num_documento}
                  onChange={(e) => handleInputChange('num_documento', e.target.value)}
                  size="small"
                  placeholder="Ex: NF-001/2025"
                  helperText="Número da nota fiscal ou documento"
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* Endereço do Estoque */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Endereço do Estoque"
                  value={formData.end_estoque}
                  onChange={(e) => handleInputChange('end_estoque', e.target.value)}
                  size="small"
                  placeholder="Ex: Prateleira A, Gaveta 3"
                  helperText="Localização no estoque"
                  disabled={loading}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Observações */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações"
                  value={formData.observacoes}
                  onChange={(e) => handleInputChange('observacoes', e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Observações sobre o equipamento..."
                  helperText="Informações adicionais (opcional)"
                  disabled={loading}
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Salvando...' : equipment ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EquipmentForm; 